<template>
  <div class="qa-item-thread-question">
    <div class="qa-item-thread-question__header">
      <div class="qa-item-thread-question__question">
        <v-row justify="space-between" align="center" no-gutters class="mb-4">
          <span
            class="qa-item-thread-question__question--id"
            data-locator="qa-question-id"
          >
            {{ `#${question.idInRoom}` }}
          </span>

          <div class="qa-item-thread-question__header-right">
            <QuestionAnswerPriorityChip
              class="mr-2"
              :priority="question.priority"
            />
            <QuestionAnswerStatusChip :status="question.status" />
          </div>
        </v-row>

        <div class="qa-item-thread-question__question--description">
          <span v-html="sanitizeHtml(question.description)"></span>
        </div>
      </div>

      <div class="qa-item-thread-question__meta">
        <div class="qa-item-thread-question__author">
          <!-- Guest side: if authors hidden AND not my question
               no initials avatar, show "Auteur masqué"
          -->
          <template v-if="shouldHideAuthor">
            <v-avatar
              v-if="$vuetify.display.mdAndUp"
              data-locator="qa-thread-author-hidden-avatar"
              size="24"
              class="mr-2"
              style="background-color: #FFEFEB; border: 1px solid #FFDFD7 !important;"
            >
              <app-material-icon
                data-locator="qa-thread-author-hidden-icon"
                name="person"
                color="brand"
                size="xs"
              />
            </v-avatar>
            <span
              class="qa-item-thread-question__username"
              data-locator="qa-thread-author-hidden-label"
            >
              {{ $t('project.question-answer.QuestionAnswerDataTable.hiddenAuthor') }}
            </span>
          </template>

          <template v-else>
            <v-avatar
              v-if="$vuetify.display.mdAndUp"
              size="24"
              class="qa-item-thread-question__initials mr-2"
              data-locator="qa-thread-author-visible-avatar"
            >
              {{ question.userShortInitials }}
            </v-avatar>
            <span
              class="qa-item-thread-question__username"
              data-locator="qa-thread-author-visible-name"
            >
              {{ question.userName }}
            </span>
          </template>
        </div>

        <div
          v-if="referenceDocuments.length > 0"
          class="qa-item-thread-question__references"
          data-locator="qa-question-reference-documents"
        >
          <div
            v-for="doc in referenceDocuments"
            :key="doc.uniqueKey"
            class="qa-item-thread-question__reference-item"
          >
            <span class="font-weight-medium">
              {{ $t('project.question-answer.QuestionAnswerItemThreadQuestion.reference') }} :
            </span>
            <span class="text-text-secondary">
              <span v-if="doc.numbering" class="mr-1">{{ doc.numbering }}</span>
              {{ doc.basename || doc.name }}
            </span>
          </div>
        </div>
      </div>

      <div class="d-flex justify-space-between align-center mt-2">
        <div class="text-caption qa-item-thread-question__creation-date">
          {{ $t('project.question-answer.QuestionAnswerItemThreadQuestion.publishedOn') }}
          <DateDisplay :value="question.createdAt" with-hours />
        </div>

        <div>
          <v-chip v-for="category in question.categories"
                  :key="category.id"
                  variant="flat"
                  size="small"
                  class="qa-item-thread-question__category-chip"
          >
            {{ category.name }}
          </v-chip>
        </div>
      </div>

      <div
        v-if="question.status !== 'validated' && (currentRoom.isPM || question.rights.canWrite)"
        class="qa-item-thread__actions"
      >
        <QuestionAnswerItemCloseQuestionDialog
          v-if="showValidateAnswerDialog"
          @close="showValidateAnswerDialog = false"
          @confirm="markQuestionAsAnswered"
        />
        <app-button
          type="outlined"
          variant="neutral"
          data-locator="close-question-button"
          :disabled="question.answers.length === 0"
          :loading="markQuestionAsResolvedPending"
          @click="showValidateAnswerDialog = true"
        >
          {{ $t('project.question-answer.QuestionAnswerItemThreadQuestion.validateAnswer') }}
        </app-button>
      </div>
    </div>
  </div>
</template>

<script>
import { mapActions, mapGetters, mapState } from 'vuex'

import DateDisplay from '@/common/DateDisplay.vue'
import { track, USER_ROLE } from '@/common/pendo/agent'
import { sanitizeHtml } from '@/common/helpers/htmlHelpers'
import { toast } from '@/common/utils/toast'
import QuestionAnswerPriorityChip from '@/project/question-answer/QuestionAnswerPriorityChip.vue'
import { QuestionAnswerActionTypes } from '@/store/modules/question-answer/action_types'

import QuestionAnswerItemCloseQuestionDialog from './QuestionAnswerItemCloseQuestionDialog.vue'
import QuestionAnswerStatusChip from './QuestionAnswerStatusChip.vue'

export default {
  name: 'QuestionAnswerItemThreadQuestion',
  components: {
    DateDisplay,
    QuestionAnswerItemCloseQuestionDialog,
    QuestionAnswerStatusChip,
    QuestionAnswerPriorityChip,
  },
  props: {
    mnemo: {
      type: String,
      required: true,
    },
  },
  data () {
    return {
      showValidateAnswerDialog: false,
    }
  },
  computed: {
    ...mapState('questionAnswer', ['question', 'markQuestionAsResolvedPending']),
    ...mapState('room', ['currentRoom']),
    ...mapGetters('room', ['isCurrentUserPm']),
    ...mapGetters('user', ['currentUserId']),
    hideQaAuthorsForCurrentUser () {
      return !this.isCurrentUserPm && Boolean(this.currentRoom?.hideQaAuthors)
    },
    shouldHideAuthor () {
      return this.hideQaAuthorsForCurrentUser && this.question?.userId !== this.currentUserId
    },
    referenceDocuments () {
      return (this.question?.files || []).map(file => ({
        ...file,
        type: 'file',
        uniqueKey: `file-${file.id}`,
        name: file.name ?? file.basename,
        basename: file.basename ?? file.name,
      }))
    },
  },
  methods: {
    ...mapActions('questionAnswer', [QuestionAnswerActionTypes.MARK_QUESTION_AS_VALIDATED]),
    sanitizeHtml,
    async markQuestionAsAnswered () {
      try {
        await this.MARK_QUESTION_AS_VALIDATED({
          mnemo: this.mnemo,
          questionId: this.question.id,
        })

        toast.success(
          this.$t(
            'project.question-answer.QuestionAnswerItemThreadQuestion.markQuestionAsAnsweredSuccess',
            { idInRoom: this.question.idInRoom },
          ),
        )
        track('question_closed', {
          project_mnemo: this.mnemo,
          question_id: this.question.id,
          user_role: this.currentRoom.isPM ? USER_ROLE.PM : USER_ROLE.GUEST,
        })
      } catch (error) {
        console.error(error)
        toast.error(this.$t('project.question-answer.QuestionAnswerItemThreadQuestion.markQuestionAsAnsweredError'))
      }
    },
  },
}
</script>

<style scoped lang="scss">
.qa-item-thread-question {
  padding-bottom: 28px;
  color: rgb(var(--v-theme-text-primary));

  &__question--id {
    font-size: 16px;
    color: rgb(var(--v-theme-text-secondary));
  }

  &__header-right {
    display: flex;
    align-items: center;
  }

  &__initials {
    color: rgb(var(--v-theme-text-inverted));
    background-color: rgb(var(--v-theme-surface-brand-bold-hover));
    font-size: 12px;
  }

  &__username {
    font-size: 16px;
    font-weight: 500;
    /* Position fixes the vertical alignment with the initials */
    position: relative;
    top: 2px;
    color: rgb(var(--v-theme-text-secondary));
  }

  &__creation-date {
    color: rgb(var(--v-theme-text-tertiary));
  }

  &__question {
    margin: 20px 0;
    font-size: 24px;
    font-weight: 500;

    // In deep selectors, it doesn't seem like the & operator can be interpreted anymore in Vue 3
    :deep(.qa-item-thread-question__question--description p) {
      margin-bottom: 0;
      overflow-wrap: anywhere;
      // Magic value that slightly increases the line-height.
      // Most UA have 1.2 as default and 1.4 seems like a good visual help
      // Especially because of the bold title-like styling
      line-height: 1.4;
    }
  }

  &__meta {
    display: flex;
    justify-content: space-between;
    align-items: flex-start;
    gap: 16px;
    min-width: 0;
  }

  &__author {
    display: flex;
    align-items: center;
    min-width: 0;
    flex: 0 1 auto;
  }

  &__references {
    display: flex;
    flex-direction: column;
    align-items: flex-end;
    gap: 4px;
    min-width: 0;
    flex: 1 1 auto;
  }

  &__reference-item {
    text-align: right;
    max-width: 50%;
  }

  &__category-chip {
    background-color: rgb(var(--v-theme-surface-grey-subtle)) !important;
    color: rgb(var(--v-theme-text-grey)) !important;
    border: 1px solid rgb(var(--v-theme-border-grey));
    font-weight: 700;
    font-size: 12px;
  }
}

@media (max-width: 959px) {
  .qa-item-thread-question {
    &__meta {
      flex-direction: column;
      align-items: stretch;
    }

    &__references {
      width: 100%;
      align-items: flex-start;
    }

    &__reference-item {
      text-align: left;
      max-width: 100%;
    }
  }
}
</style>
